import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { superAdminAPI, channelPartnerAdminAPI } from "../../services/api";
import { Card, Button, Spinner } from "../UI";
import PasswordInput from "./PasswordInput";
import {
  HiOutlineCreditCard,
  HiOutlineCheckCircle,
  HiOutlineExclamation,
  HiOutlineLightningBolt,
} from "react-icons/hi";

/**
 * SuperAdmin → Settings → Razorpay
 *
 * Configures the Razorpay Key ID/Secret (checkout + subscriptions), webhook
 * secret, and the RazorpayX account number (automated channel-partner
 * payouts). Saved settings apply immediately (no restart).
 */

const inputClass =
  "w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-50";

const StatusLine = ({ ok, children }) => (
  <p className={`flex items-start gap-1.5 text-xs ${ok ? "text-green-700" : "text-amber-700"}`}>
    {ok ? (
      <HiOutlineCheckCircle className="w-4 h-4 flex-shrink-0" />
    ) : (
      <HiOutlineExclamation className="w-4 h-4 flex-shrink-0" />
    )}
    <span>{children}</span>
  </p>
);

const WEEKDAYS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * WHEN the daily/weekly/monthly autopay batch actually runs (platform-wide,
 * server local time). WHO is on which cadence is set per partner — see the
 * "Auto-payout via RazorpayX" option on the Channel Partner create/edit form.
 */
const ChannelPartnerAutopayScheduleSection = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["sa-channel-partner-autopay-schedule"],
    queryFn: async () => (await channelPartnerAdminAPI.getAutopaySchedule()).data?.data,
  });

  useEffect(() => {
    if (data) {
      setForm({
        hour: data.hour ?? 10,
        minute: data.minute ?? 30,
        weeklyDay: data.weeklyDay ?? 1,
        monthlyDay: data.monthlyDay ?? 1,
      });
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (body) => channelPartnerAdminAPI.updateAutopaySchedule(body),
    onSuccess: (response) => {
      queryClient.setQueryData(["sa-channel-partner-autopay-schedule"], response.data?.data);
      toast.success("Autopay schedule saved");
    },
    onError: (err) => toast.error(err.response?.data?.message || "Failed to save autopay schedule"),
  });

  if (isLoading || !form) {
    return (
      <div className="flex justify-center py-4">
        <Spinner />
      </div>
    );
  }
  if (error) {
    return <StatusLine ok={false}>{error.response?.data?.message || "Failed to load autopay schedule"}</StatusLine>;
  }

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const isDirty =
    form.hour !== (data.hour ?? 10) ||
    form.minute !== (data.minute ?? 30) ||
    form.weeklyDay !== (data.weeklyDay ?? 1) ||
    form.monthlyDay !== (data.monthlyDay ?? 1);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Time of day</label>
          <div className="flex items-center gap-1">
            <select
              className={inputClass}
              value={form.hour}
              onChange={(e) => set({ hour: parseInt(e.target.value, 10) })}
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>{pad2(h)}</option>
              ))}
            </select>
            <span className="text-gray-400">:</span>
            <select
              className={inputClass}
              value={form.minute}
              onChange={(e) => set({ minute: parseInt(e.target.value, 10) })}
            >
              {[0, 15, 30, 45].map((m) => (
                <option key={m} value={m}>{pad2(m)}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Weekly run day</label>
          <select
            className={inputClass}
            value={form.weeklyDay}
            onChange={(e) => set({ weeklyDay: parseInt(e.target.value, 10) })}
          >
            {WEEKDAYS.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Monthly run day</label>
          <select
            className={inputClass}
            value={form.monthlyDay}
            onChange={(e) => set({ monthlyDay: parseInt(e.target.value, 10) })}
          >
            {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
      </div>
      <p className="text-xs text-gray-500">
        Daily-cadence partners are paid every day at {pad2(form.hour)}:{pad2(form.minute)}. Weekly-cadence
        partners also get included every {WEEKDAYS.find((d) => d.value === form.weeklyDay)?.label}. Monthly-cadence
        partners also get included on day {form.monthlyDay} of each month.
      </p>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-xs text-gray-400">
          {data.updatedAt
            ? `Last changed ${new Date(data.updatedAt).toLocaleString()}${data.updatedBy ? ` by ${data.updatedBy}` : ""}`
            : "Defaults to 10:30 AM, weekly on Monday, monthly on the 1st"}
        </p>
        <Button
          size="sm"
          onClick={() => saveMutation.mutate(form)}
          loading={saveMutation.isPending}
          disabled={!isDirty || saveMutation.isPending}
        >
          Save schedule
        </Button>
      </div>
    </div>
  );
};

const RazorpaySettings = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null);
  const [newLiveKeySecret, setNewLiveKeySecret] = useState("");
  const [newTestKeySecret, setNewTestKeySecret] = useState("");
  const [newWebhookSecret, setNewWebhookSecret] = useState("");
  const [testing, setTesting] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["sa-razorpay-settings"],
    queryFn: async () => (await superAdminAPI.getRazorpaySettings()).data?.data,
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      mode: data.mode || "live",
      liveKeyId: data.live?.keyId || "",
      testKeyId: data.test?.keyId || "",
      accountNumber: data.accountNumber || "",
    });
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (body) => superAdminAPI.updateRazorpaySettings(body),
    onSuccess: (response) => {
      setNewLiveKeySecret("");
      setNewTestKeySecret("");
      setNewWebhookSecret("");
      queryClient.setQueryData(["sa-razorpay-settings"], response.data?.data);
      toast.success("Razorpay settings saved — used for the next payment");
    },
    onError: (err) => toast.error(err.response?.data?.message || "Failed to save Razorpay settings"),
  });

  // Switching mode alone (no other edits) saves immediately — this is the
  // "flip to test for a couple minutes, then flip back" control, so it
  // shouldn't require also hitting the main Save button below.
  const switchMode = (mode) => {
    if (mode === form.mode) return;
    set({ mode });
    saveMutation.mutate({ mode });
  };

  const runTest = async () => {
    setTesting(true);
    try {
      const response = await superAdminAPI.testRazorpaySettings();
      toast.success(response.data?.message || "Razorpay credentials are valid");
    } catch (err) {
      toast.error(err.response?.data?.message || "Razorpay test failed", { duration: 6000 });
    } finally {
      setTesting(false);
    }
  };

  if (isLoading || (!form && !error)) {
    return (
      <Card padding="p-6" className="flex justify-center">
        <Spinner />
      </Card>
    );
  }
  if (error) {
    return (
      <Card padding="p-6">
        <StatusLine ok={false}>
          {error.response?.data?.message || "Failed to load Razorpay settings"}
        </StatusLine>
      </Card>
    );
  }

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const isDirty =
    newLiveKeySecret.trim() !== "" ||
    newTestKeySecret.trim() !== "" ||
    newWebhookSecret.trim() !== "" ||
    form.liveKeyId !== (data.live?.keyId || "") ||
    form.testKeyId !== (data.test?.keyId || "") ||
    form.accountNumber !== (data.accountNumber || "");

  const save = () => {
    const body = { mode: form.mode, liveKeyId: form.liveKeyId, testKeyId: form.testKeyId, accountNumber: form.accountNumber };
    if (newLiveKeySecret.trim()) body.liveKeySecret = newLiveKeySecret.trim();
    if (newTestKeySecret.trim()) body.testKeySecret = newTestKeySecret.trim();
    if (newWebhookSecret.trim()) body.webhookSecret = newWebhookSecret.trim();
    saveMutation.mutate(body);
  };

  const clearLiveKeySecret = () => {
    if (!window.confirm("Remove the saved Razorpay LIVE Key Secret? Live mode will use the server .env key instead.")) return;
    saveMutation.mutate({ clearLiveKeySecret: true });
  };

  const clearTestKeySecret = () => {
    if (!window.confirm("Remove the saved Razorpay TEST Key Secret?")) return;
    saveMutation.mutate({ clearTestKeySecret: true });
  };

  const clearWebhookSecret = () => {
    if (!window.confirm("Remove the saved Razorpay webhook secret? Webhook signature verification will be skipped until a new one is set.")) return;
    saveMutation.mutate({ clearWebhookSecret: true });
  };

  return (
    <Card padding="p-4 sm:p-6" className="space-y-6">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
          <HiOutlineCreditCard className="w-5 h-5 text-purple-600" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-bold text-gray-900">Razorpay</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Credentials used for checkout, subscription renewals, and channel partner
            payouts (RazorpayX). Changes apply to the next payment — no server restart needed.
          </p>
        </div>
      </div>

      {/* Live/Test mode switch */}
      <div className="space-y-3 border-t border-gray-100 pt-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-gray-800">Active Mode</h3>
          <div className="inline-flex rounded-lg border border-gray-300 p-0.5 bg-gray-50">
            <button
              type="button"
              onClick={() => switchMode("live")}
              className={`px-3.5 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                form.mode === "live" ? "bg-green-600 text-white shadow-sm" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Live
            </button>
            <button
              type="button"
              onClick={() => switchMode("test")}
              className={`px-3.5 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                form.mode === "test" ? "bg-amber-500 text-white shadow-sm" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Test
            </button>
          </div>
        </div>
        {form.mode === "test" ? (
          <StatusLine ok={false}>
            <strong>TEST MODE is active</strong> — real checkout traffic is using the test credentials below.
            Switch back to Live when you're done testing.
          </StatusLine>
        ) : (
          <StatusLine ok>Live mode is active — real checkout traffic uses the live credentials below.</StatusLine>
        )}
      </div>

      {/* Live credentials */}
      <div className="space-y-3 border-t border-gray-100 pt-5">
        <h3 className="text-sm font-semibold text-gray-800">Live API Keys</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="rzp-live-key-id">Key ID</label>
            <input
              id="rzp-live-key-id"
              className={inputClass}
              value={form.liveKeyId}
              placeholder={data.live?.effectiveKeyId ? `Using: ${data.live.effectiveKeyId}` : "rzp_live_…"}
              onChange={(e) => set({ liveKeyId: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="rzp-live-key-secret">Key Secret</label>
            <PasswordInput
              id="rzp-live-key-secret"
              className={inputClass}
              value={newLiveKeySecret}
              autoComplete="off"
              placeholder={data.live?.keySecretSet ? `Saved: ${data.live.keySecretMasked} — enter to replace` : "Key secret"}
              onChange={(e) => setNewLiveKeySecret(e.target.value)}
            />
          </div>
        </div>
        {data.live?.keySecretSet ? (
          <StatusLine ok>
            Live key secret saved ({data.live.keySecretMasked}), stored encrypted.{" "}
            <button type="button" onClick={clearLiveKeySecret} className="underline text-red-600">Remove</button>
          </StatusLine>
        ) : data.live?.envKeySet ? (
          <StatusLine ok>Using the live Key ID/Secret from the server .env.</StatusLine>
        ) : (
          <StatusLine ok={false}>No live credentials configured yet.</StatusLine>
        )}
      </div>

      {/* Test credentials */}
      <div className="space-y-3 border-t border-gray-100 pt-5">
        <h3 className="text-sm font-semibold text-gray-800">Test API Keys</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="rzp-test-key-id">Key ID</label>
            <input
              id="rzp-test-key-id"
              className={inputClass}
              value={form.testKeyId}
              placeholder={data.test?.effectiveKeyId ? `Using: ${data.test.effectiveKeyId}` : "rzp_test_…"}
              onChange={(e) => set({ testKeyId: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="rzp-test-key-secret">Key Secret</label>
            <PasswordInput
              id="rzp-test-key-secret"
              className={inputClass}
              value={newTestKeySecret}
              autoComplete="off"
              placeholder={data.test?.keySecretSet ? `Saved: ${data.test.keySecretMasked} — enter to replace` : "Key secret"}
              onChange={(e) => setNewTestKeySecret(e.target.value)}
            />
          </div>
        </div>
        {data.test?.keySecretSet ? (
          <StatusLine ok>
            Test key secret saved ({data.test.keySecretMasked}), stored encrypted.{" "}
            <button type="button" onClick={clearTestKeySecret} className="underline text-red-600">Remove</button>
          </StatusLine>
        ) : data.test?.envKeySet ? (
          <StatusLine ok>Using the test Key ID/Secret from the server .env.</StatusLine>
        ) : (
          <StatusLine ok={false}>No test credentials configured yet.</StatusLine>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={runTest}
          loading={testing}
          disabled={testing || isDirty}
          title={isDirty ? "Save first, then test" : undefined}
        >
          Test Razorpay connection ({form.mode})
        </Button>
      </div>

      {/* Webhook secret */}
      <div className="space-y-3 border-t border-gray-100 pt-5">
        <h3 className="text-sm font-semibold text-gray-800">Webhook Secret</h3>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="rzp-webhook-secret">
            Webhook secret (from Razorpay Dashboard → Webhooks)
          </label>
          <PasswordInput
            id="rzp-webhook-secret"
            className={inputClass}
            value={newWebhookSecret}
            autoComplete="off"
            placeholder={data.webhookSecretSet ? `Saved: ${data.webhookSecretMasked} — enter to replace` : "Webhook secret"}
            onChange={(e) => setNewWebhookSecret(e.target.value)}
          />
        </div>
        {data.webhookSecretSet ? (
          <StatusLine ok>
            Webhook secret saved ({data.webhookSecretMasked}), stored encrypted.{" "}
            <button type="button" onClick={clearWebhookSecret} className="underline text-red-600">Remove</button>
          </StatusLine>
        ) : (
          <StatusLine ok={false}>
            No webhook secret set — incoming webhook signatures won't be verified.
          </StatusLine>
        )}
      </div>

      {/* RazorpayX */}
      <div className="space-y-3 border-t border-gray-100 pt-5">
        <h3 className="text-sm font-semibold text-gray-800">RazorpayX (Channel Partner Payouts)</h3>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="rzp-account-number">
            RazorpayX account number
          </label>
          <input
            id="rzp-account-number"
            className={inputClass}
            value={form.accountNumber}
            placeholder={data.effectiveAccountNumber ? `Using: ${data.effectiveAccountNumber}` : "Virtual account number"}
            onChange={(e) => set({ accountNumber: e.target.value })}
          />
        </div>
        <StatusLine ok={!!data.effectiveAccountNumber}>
          {data.effectiveAccountNumber
            ? "RazorpayX payouts are enabled for channel partner commissions."
            : "Not set — channel partner payouts will require manual mark-as-paid. RazorpayX must also be activated on your Razorpay account."}
        </StatusLine>
      </div>

      {/* Channel Partner Autopay — WHO/HOW OFTEN per partner, WHEN here */}
      <div className="space-y-3 border-t border-gray-100 pt-5">
        <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
          <HiOutlineLightningBolt className="w-4 h-4 text-amber-500" />
          Channel Partner Autopay Schedule
        </h3>
        <p className="text-xs text-gray-500">
          Whether a partner gets autopay, and on which cadence (immediately / daily / weekly / monthly),
          is set on each partner individually — see "Auto-payout via RazorpayX" on the Channel Partner
          create/edit form. This only controls what time the daily/weekly/monthly batches actually run.
        </p>
        <ChannelPartnerAutopayScheduleSection />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-t border-gray-100 pt-5">
        <p className="text-xs text-gray-400">
          {data.updatedAt
            ? `Last changed ${new Date(data.updatedAt).toLocaleString()}${data.updatedBy ? ` by ${data.updatedBy}` : ""}`
            : "Using server .env defaults"}
        </p>
        <Button onClick={save} loading={saveMutation.isPending} disabled={!isDirty || saveMutation.isPending}>
          Save Razorpay settings
        </Button>
      </div>
    </Card>
  );
};

export default RazorpaySettings;
